import { useMutation } from '@apollo/client'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import { PageHeader } from 'components/page-header'
import { FormActionBar } from 'components/form-util'
import { useIsMobile } from 'lib/hooks'
import { useHistory, useRouteMatch } from 'react-router-dom'
import * as yup from 'yup'
import { UPDATE_STUDENT } from './api-hooks'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import { useSnackbar } from 'notistack'
import FormContent from './form-container'
import {
  AgeTiles,
  ContactInfoFields,
  GuardianNameFields,
  StudentNameFields,
} from './form-fields'
import { gtmEvent } from 'lib/gtm'
import { stripPhoneNumber } from 'utils/phoneNumber'
import useStyles from './styles'

const validationSchema = yup.object().shape({
  firstName: yup
    .string()
    .min(1)
    .required(),
  lastName: yup
    .string()
    .min(1)
    .required(),
  email: yup
    .string()
    .email()
    .required(),
  phone: yup.string().matches(/^\d{3}-?\d{3}-?\d{4}$/, {
    message: 'Phone number must be 10 digits',
    excludeEmptyString: true,
  }),
  guardianFirstName: yup.string().min(1),
  guardianLastName: yup.string().min(1),
})

const formatPhoneNumber = num => {
  if (num) {
    const cleanedNum = num.replace(/\D/g, '')
    return `${cleanedNum}`
  }
  return null
}

export default function EditStudentForm({ student }) {
  const isMobile = useIsMobile()
  const classes = useStyles()
  const { path } = useRouteMatch()
  const history = useHistory()
  const { enqueueSnackbar } = useSnackbar()

  const studentName = `${student?.firstName} ${student?.lastName}`
  const isMinor = student.isMinor
  const { contact = {} } = student

  const [updateStudent] = useMutation(UPDATE_STUDENT)

  const form = useForm({
    defaultValues: {
      firstName: student.firstName || '',
      lastName: student.lastName || '',
      email: contact?.email || '',
      phone: stripPhoneNumber(contact?.phoneNumber) || '',
      guardianFirstName: contact?.firstName,
      guardianLastName: contact?.lastName,
    },
    resolver: yupResolver(validationSchema),
  })

  const { handleSubmit, formState } = form
  const { dirtyFields } = formState

  const url = `${path
    .split('/')
    .slice(0, 3)
    .join('/')}/${student.id}`

  const onSubmit = async formValues => {
    formValues.phone = formatPhoneNumber(formValues.phone)
    try {
      await updateStudent({
        variables: { studentId: student.id, inputs: formValues },
      }).then(response => {
        if (response.data?.updateStudent.success) {
          gtmEvent({
            event: 'formSubmit',
            formCategory: 'edit-student',
            formAction: 'coach-edited-student',
            metaData: dirtyFields,
          })
          enqueueSnackbar('Student updated', {
            variant: 'success',
          })
          history.push(url)
        } else {
          enqueueSnackbar(response.data?.updateStudent.message, {
            variant: 'error',
          })
        }
      })
    } catch (e) {
      enqueueSnackbar('Sorry, the student could not be updated', {
        variant: 'error',
      })
    }
  }

  const crumbs = [
    {
      label: 'Students',
      to: `${path
        .split('/')
        .slice(0, 2)
        .join('/')}/students/list`,
    },
    {
      label: studentName,
      to: url,
    },
  ]

  return (
    <Box className={classes.root}>
      <PageHeader
        title="Edit Student Details"
        crumbs={crumbs}
        isMobile={isMobile}
      />
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormActionBar
            includeCancel
            onCancel={() => history.push(url)}
            stickToBottom={isMobile}
          >
            <Button variant="contained" color="primary" type="submit">
              Save
            </Button>
          </FormActionBar>
          <Box className={classes.formContainer}>
            <FormContent eyebrow="Student information">
              <AgeTiles isMinor={isMinor} />
              <Box className={classes.formFieldsSection}>
                <StudentNameFields />
                {!isMinor && <ContactInfoFields />}
                {isMinor && (
                  <>
                    <Typography className={classes.sectionLabel}>
                      Guardian Information
                    </Typography>
                    <GuardianNameFields />
                    <ContactInfoFields />
                  </>
                )}
              </Box>
            </FormContent>
          </Box>
        </form>
      </FormProvider>
    </Box>
  )
}
